import { useEffect, useId, useRef, type ReactNode, type RefObject } from "react";
import { cx } from "./index";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal focus handling shared by dialogs and drawers: moves focus in when opened (to
 * `initialFocus` or the first focusable element), keeps Tab inside, closes on Escape, and gives
 * focus back to whatever had it before.
 */
export function useModalFocus(panel: RefObject<HTMLElement | null>, onClose: () => void, initialFocus?: RefObject<HTMLElement | null>) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = panel.current;
    if (root === null) return undefined;
    const first = initialFocus?.current ?? root.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? root).focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const items = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const head = items[0];
      const tail = items[items.length - 1];
      if (head === undefined || tail === undefined) {
        e.preventDefault();
        return;
      }
      if (e.shiftKey && document.activeElement === head) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && document.activeElement === tail) {
        e.preventDefault();
        head.focus();
      }
    };
    root.addEventListener("keydown", onKey);
    return () => {
      root.removeEventListener("keydown", onKey);
      if (previous !== null && previous.isConnected) previous.focus();
    };
  }, [panel, initialFocus]);
}

interface OverlayProps {
  title: string;
  description?: string;
  onClose: () => void;
  initialFocus?: RefObject<HTMLElement | null>;
  children: ReactNode;
}

/** A centred modal dialog (mount it only while open). */
export function Dialog({ title, description, onClose, initialFocus, children }: OverlayProps) {
  return (
    <Overlay title={title} onClose={onClose} {...(description === undefined ? {} : { description })} {...(initialFocus === undefined ? {} : { initialFocus })} placement="center">
      {children}
    </Overlay>
  );
}

/** A panel sliding in from the right; full width on a phone (mount it only while open). */
export function Drawer({ title, description, onClose, initialFocus, children }: OverlayProps) {
  return (
    <Overlay title={title} onClose={onClose} {...(description === undefined ? {} : { description })} {...(initialFocus === undefined ? {} : { initialFocus })} placement="right">
      {children}
    </Overlay>
  );
}

function Overlay({ title, description, onClose, initialFocus, children, placement }: OverlayProps & { placement: "center" | "right" }) {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useModalFocus(panel, onClose, initialFocus);

  return (
    <div className={cx("fixed inset-0 z-50 flex", placement === "center" ? "items-center justify-center p-4" : "justify-end")}>
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" onClick={onClose} />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descriptionId}
        tabIndex={-1}
        className={cx(
          "relative flex flex-col gap-4 overflow-y-auto border border-border bg-surface p-5 shadow-lg",
          placement === "center" ? "max-h-[90dvh] w-full max-w-md rounded-xl" : "h-dvh w-full max-w-md",
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-text">
              {title}
            </h2>
            {description === undefined ? null : (
              <p id={descriptionId} className="mt-1 text-sm text-muted">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 shrink-0 place-items-center rounded-md text-muted hover:bg-surface-2"
          >
            <svg aria-hidden="true" viewBox="0 0 20 20" className="size-4" fill="currentColor">
              <path d="M5.3 4 10 8.7 14.7 4 16 5.3 11.3 10l4.7 4.7-1.3 1.3-4.7-4.7L5.3 16 4 14.7 8.7 10 4 5.3z" />
            </svg>
            <span className="sr-only">Close</span>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
